<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import ProjectService from '@/services/ProjectService'
import Spinner from '@/components/ui/Spinner.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import ProjectDot from './ProjectDot.vue'
import ProjectTile from './ProjectTile.vue'

const store = useStore()

const usage = ref(null)
const loading = ref(false)
const failed = ref(false)

// Only worth showing — and only worth a request — once the account actually
// splits work across workspaces. Guests and single-project owners never see a
// heading flash in and out while a fetch they cannot use resolves.
const eligible = computed(() =>
  store.getters['Projects/showProjectSwitcher'] && store.getters['Projects/hasMultipleProjects'],
)

const projects = computed(() => usage.value?.workspaces || [])
const totals = computed(() => usage.value?.totals || {})
const pool = computed(() => usage.value?.pool || {})

/**
 * How many workspaces the plan allows, as set per plan (and overridable per
 * account) in the manager app. The bars below describe a pool that is shared;
 * this is the one plan limit on the page that is about the workspaces
 * themselves, so it belongs beside the heading rather than in a bar.
 */
const allowance = computed(() => store.getters['Projects/projectAllowance'])
const allowanceLabel = computed(() => {
  const limit = Number(allowance.value?.limit) || 0
  if (!limit) return null
  const used = store.getters['Projects/projects'].length || Number(allowance.value?.used) || 0
  return `${used} of ${limit} ${limit === 1 ? 'workspace' : 'workspaces'} used on your plan`
})

const isVisible = computed(() => eligible.value && projects.value.length > 1)

const gb = (value) => (value || 0) / 1000 / 1000 / 1000

const formatGb = (value) => {
  // Null is "we could not read this", which is not the same as none, and
  // printing it as 0 GB told people a workspace had served nothing all cycle.
  if (value === null || value === undefined) return '—'
  const amount = gb(value)
  if (!amount) return '0 GB'
  return `${amount < 10 ? amount.toFixed(2).replace(/\.?0+$/, '') : Math.round(amount)} GB`
}

/**
 * Width of one workspace's slice. The slices stack because the pool is shared:
 * they add up to the account's usage, with the remainder left as available.
 * Drawn against the account's own total when the plan states no limit, since
 * then there is nothing else to size them by. A workspace with real usage gets a
 * sliver rather than a hairline the eye reads as empty.
 */
const widthOf = (used, poolTotal, usedTotal) => {
  const scale = Math.max(poolTotal || 0, usedTotal || 0)
  if (!scale || !used) return 0
  return Math.min(Math.max((used / scale) * 100, 2), 100)
}

/** What the hover quotes: what the workspace costs against the whole plan. */
const shareOfPlan = (used, poolTotal) => (poolTotal ? Math.round(((used || 0) / poolTotal) * 100) : 0)

/**
 * Why the default workspace's figures can exceed what its own content adds up
 * to. Usage the plan is billed for that belongs to no single workspace — the
 * account-wide assets bucket, and origin bandwidth, which is metered per
 * account and never per stream — is counted there, so every byte on the plan
 * sits under some workspace.
 */
const accountWideNote = (project) => {
  const parts = []
  if (project.accountWideStorageBytes) parts.push(`${formatGb(project.accountWideStorageBytes)} of storage`)
  if (project.accountWideBandwidthBytes) parts.push(`${formatGb(project.accountWideBandwidthBytes)} of bandwidth`)
  if (!parts.length) return null
  return `Includes ${parts.join(' and ')} used across the whole account, which belongs to no single workspace.`
}

const streamLimit = computed(() => Number(pool.value.streams) || 0)

// Bytes read the same in the caption and the hover; a stream count needs the
// noun once it is out of the bar's own context.
const BYTES = { caption: formatGb, hover: formatGb }
const COUNT = {
  caption: (value) => `${value || 0}`,
  hover: (value) => `${value || 0} ${value === 1 ? 'stream' : 'streams'}`,
}

/**
 * One bar per allowance the plan shares between workspaces. Below the limit it
 * is split by workspace, so it is clear who is drawing on the pool, with the
 * remainder left as available. Once the limit is reached the split gives way
 * to a single red fill: from there the only thing worth saying is that the
 * plan has run out, which is how the usage bars on Billing read too. The
 * per-project figures stay in the table below either way.
 */
const usageBar = (key, label, field, poolTotal, format) => {
  const usedTotal = totals.value[field] || 0
  const over = poolTotal > 0 && usedTotal >= poolTotal

  return {
    key,
    label,
    over,
    caption: poolTotal
      ? `${format.caption(usedTotal)} used of ${format.caption(poolTotal)}`
      : `${format.caption(usedTotal)} across ${projects.value.length} workspaces`,
    segments: over
      ? [{ id: key, percent: 100, class: 'bg-error-light' }]
      : projects.value.map((project) => ({
        id: project.id,
        color: project.color,
        percent: widthOf(project[field], poolTotal, usedTotal),
        tooltip: `${project.name} — ${format.hover(project[field])} (${shareOfPlan(project[field], poolTotal)}% of plan)`,
      })),
  }
}

const bars = computed(() => [
  usageBar('storage', 'Storage', 'storageBytes', pool.value.storageBytes || 0, BYTES),
  usageBar('bandwidth', 'Bandwidth', 'bandwidthBytes', pool.value.bandwidthBytes || 0, BYTES),
  usageBar('streams', 'Streams', 'streams', streamLimit.value, COUNT),
])

/** Keeps the totals row saying the same thing as the bar above it. */
const overClass = (key) => (bars.value.find((bar) => bar.key === key)?.over ? 'text-error-light' : '')

/**
 * Said out loud rather than papered over: the plan's own total is still right,
 * but with a workspace's figure missing the rows below it no longer account for
 * all of it, and a reader comparing the two deserves to know why.
 */
const incompleteNote = computed(() => {
  const missing = []
  if (totals.value.storageComplete === false) missing.push('Storage')
  if (totals.value.bandwidthComplete === false) missing.push('Bandwidth')
  if (!missing.length) return null
  return `${missing.join(' and ')} could not be read for every workspace just now, so the rows do not add up to the total. Refresh to try again.`
})

let requested = false
const load = async () => {
  if (requested) return
  requested = true
  loading.value = true
  try {
    usage.value = await ProjectService.getProjectUsage()
  } catch (error) {
    failed.value = true
    console.error('[Projects] Failed to load usage:', error)
  } finally {
    loading.value = false
  }
}

watch(eligible, (ok) => { if (ok) load() }, { immediate: true })
</script>

<template>
  <section v-if="eligible && !failed" class="mt-6">
    <div class="flex items-baseline justify-between gap-3 mb-1">
      <h4 class="text-tiny uppercase tracking-wide text-surface-7 font-medium mb-0">Usage by workspace</h4>
      <span v-if="allowanceLabel" class="text-tiny text-surface-6 shrink-0" data-testid="workspace-allowance">
        {{ allowanceLabel }}
      </span>
    </div>
    <p class="text-sm text-surface-6 mb-4">
      Storage, bandwidth and streams come from one plan and are shared by all your workspaces. This
      is how much of it each workspace is using.
    </p>

    <Spinner
      v-if="loading"
      text="Loading workspace usage…"
      classes="text-dark-8"
      spinner-color="var(--c-dark-8)"
      spinner-size="15px"
    />

    <template v-else-if="isVisible">
      <div class="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
        <span
          v-for="project in projects"
          :key="project.id"
          class="inline-flex items-center gap-1.5 text-tiny text-surface-8"
        >
          <ProjectDot :color="project.color" />
          {{ project.name }}
        </span>
        <span class="inline-flex items-center gap-1.5 text-tiny text-surface-6">
          <ProjectDot color="var(--c-surface-3)" />
          Available
        </span>
      </div>

      <div class="space-y-3">
        <div v-for="bar in bars" :key="bar.key" class="bg-surface-2 rounded-lg p-3" :data-testid="`usage-${bar.key}`">
          <div class="flex items-baseline justify-between gap-2 mb-2">
            <p class="text-tiny text-surface-7 mb-0">{{ bar.label }}</p>
            <p class="text-tiny mb-0" :class="bar.over ? 'text-error-light font-medium' : 'text-surface-6'">
              {{ bar.caption }}
            </p>
          </div>
          <div class="flex h-2 w-full overflow-hidden rounded-full bg-surface-3">
            <Tooltip
              v-for="segment in bar.segments"
              :key="segment.id"
              :content="segment.tooltip"
              wrapper-class="block h-full w-full"
              :style="{ width: `${segment.percent}%` }"
            >
              <span
                class="block h-full w-full"
                :class="segment.class"
                :style="{ backgroundColor: segment.color }"
              />
            </Tooltip>
          </div>
        </div>
      </div>

      <div class="bg-surface-1 rounded-xl overflow-hidden mt-4">
        <div class="hidden md:flex text-tiny text-surface-6 px-4 py-2.5">
          <span class="basis-4/12">Workspace</span>
          <span class="basis-2/12">Streams</span>
          <span class="basis-3/12">Storage used</span>
          <span class="basis-3/12">Bandwidth used</span>
        </div>
        <div
          v-for="project in projects"
          :key="project.id"
          class="hidden md:flex text-sm text-surface-9 items-center px-4 py-2.5"
        >
          <span class="basis-4/12 inline-flex items-center gap-2 min-w-0 pr-3">
            <ProjectTile :name="project.name" :color="project.color" size="size-6" textClass="text-[10px]" />
            <span class="truncate">{{ project.name }}</span>
            <span v-if="accountWideNote(project)" :title="accountWideNote(project)" class="text-surface-6 shrink-0">*</span>
          </span>
          <span class="basis-2/12 tabular-nums">{{ project.streams }}</span>
          <span class="basis-3/12 tabular-nums" :title="accountWideNote(project)">{{ formatGb(project.storageBytes) }}</span>
          <span class="basis-3/12 tabular-nums" :title="accountWideNote(project)">{{ formatGb(project.bandwidthBytes) }}</span>
        </div>
        <div class="hidden md:flex text-sm items-center px-4 py-2.5 border-t border-surface-3">
          <span class="basis-4/12 text-surface-7">Total used</span>
          <span class="basis-2/12 tabular-nums" :class="overClass('streams')">{{ totals.streams || 0 }}</span>
          <span class="basis-3/12 tabular-nums" :class="overClass('storage')">{{ formatGb(totals.storageBytes) }}</span>
          <span class="basis-3/12 tabular-nums" :class="overClass('bandwidth')">{{ formatGb(totals.bandwidthBytes) }}</span>
        </div>
        <!-- The total is the plan's own, so it still stands when a workspace
             could not be read; the rows just will not add up to it. -->
        <p v-if="incompleteNote" class="px-4 pb-2.5 mb-0 text-tiny text-surface-6">{{ incompleteNote }}</p>
        <div class="hidden md:flex text-sm text-surface-6 items-center px-4 py-2.5">
          <span class="basis-4/12">Plan{{ pool.planName ? ` (${pool.planName})` : '' }}</span>
          <span class="basis-2/12 tabular-nums">{{ streamLimit || '—' }}</span>
          <span class="basis-3/12 tabular-nums">{{ formatGb(pool.storageBytes) }}</span>
          <span class="basis-3/12 tabular-nums">{{ formatGb(pool.bandwidthBytes) }}</span>
        </div>

        <div class="divide-y divide-surface-3 md:hidden">
          <article v-for="project in projects" :key="`mobile-${project.id}`" class="p-4 text-sm text-surface-9">
            <p class="inline-flex items-center gap-2 font-medium mb-0">
              <ProjectTile :name="project.name" :color="project.color" size="size-6" textClass="text-[10px]" />
              {{ project.name }}
            </p>
            <p class="mt-1 text-surface-7 mb-0">{{ project.streams }} streams</p>
            <p class="mt-1 text-surface-7 mb-0">
              {{ formatGb(project.storageBytes) }} storage · {{ formatGb(project.bandwidthBytes) }} bandwidth
            </p>
          </article>
        </div>
      </div>
    </template>
  </section>
</template>
